<script>
  import { _ } from "svelte-i18n";
  import {
    formatIeltsBand,
    ieltsReadingBandLevel,
    ieltsBandNameIndex,
  } from "../../ieltsReading.mjs";

  export let instanceData;
  export let band = null;
  // READING | LISTENING | WRITING | SPEAKING — selects the title, band label
  // and level-descriptor i18n keys. The band thresholds behind the level
  // tiers are the same for all four skills.
  export let skill = "READING";
  // False when the candidate submitted nothing at all. A band can still be
  // reported for an unanswered paper, and the level descriptors all describe
  // an ability the candidate demonstrated, so an unattempted paper needs its
  // own wording rather than the bottom tier's.
  export let attempted = true;

  const dateOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };

  $: bandLevel = ieltsReadingBandLevel(band);
  $: bandNameIndex = ieltsBandNameIndex(band);
  $: tooltipId = `ielts-${skill.toLowerCase()}-band-criteria`;

  // finishAt is a naive UTC timestamp (same contract formatDateToLocale in
  // FormatUtils.svelte handles by appending "Z").
  function formatFinishDate(timestamp) {
    if (!timestamp) return "";
    const iso = String(timestamp).endsWith("Z") ? timestamp : timestamp + "Z";
    const date = new Date(iso);
    return isNaN(date.getTime())
      ? ""
      : date.toLocaleDateString("en-US", dateOptions);
  }
</script>

<!-- No print button: the browser's own print command (Ctrl/Cmd+P) already
     produces the clean report — the print rules in global.css open every
     collapsed section. -->
<div class="row justify-content-center">
  <div class="col text-center">
    <img
      src="assets/img/logo_horizontal_nobg2.png"
      width="200px"
      alt="logo"
    />
  </div>
</div>

<div class="row mt-3">
  <div class="col text-center">
    <h1 class="report-title">{$_("IELTS_" + skill + "_REPORT_TITLE")}</h1>
  </div>
</div>

<hr class="mb-4" />

<div class="candidate-row">
  <div class="candidate-details">
    <h2 class="candidate-name">{instanceData?.clientName}</h2>
    <h6 class="text-muted mb-0">
      {formatFinishDate(instanceData?.finishAt)}
    </h6>
  </div>

  <!-- Fixed-width card so the band sits in exactly the same place on every
       IELTS report, whatever the candidate name or band value. -->
  <div class="band-card">
    <div class="band-label">
      {$_("IELTS_" + skill + "_BAND")}<button
        type="button"
        class="band-info"
        aria-label={$_("IELTS_HOW_THIS_IS_ASSESSED")}
        aria-describedby={tooltipId}
        ><i class="fas fa-info-circle" aria-hidden="true" /><span
          class="band-tooltip"
          id={tooltipId}
          role="tooltip"
          ><span class="band-tooltip-title"
            >{$_("IELTS_HOW_THIS_IS_ASSESSED")}</span
          >{$_("IELTS_" + skill + "_CRITERIA")}
          {$_("IELTS_BAND_SCALE_NOTE")}</span
        ></button
      >
    </div>
    <div class="band-score">{formatIeltsBand(band)}</div>
    {#if bandNameIndex !== null}
      <div class="band-name">{$_("IELTS_BAND_NAME_" + bandNameIndex)}</div>
    {/if}
  </div>
</div>

<div class="feedback ielts-avoid-break">
  {#if bandNameIndex !== null}
    <div class="feedback-label">{$_("IELTS_BAND_DESCRIPTOR")}</div>
    <p class="feedback-text">
      {$_("IELTS_BAND_DESCRIPTOR_" + bandNameIndex)}
    </p>
  {/if}

  {#if !attempted}
    <div class="feedback-label">{$_("IELTS_WHAT_THIS_MEANS")}</div>
    <p class="feedback-text mb-0">{$_("IELTS_NOT_ATTEMPTED_MEANING")}</p>
  {:else if bandLevel !== null}
    <div class="feedback-label">{$_("IELTS_WHAT_THIS_MEANS")}</div>
    <p class="feedback-text mb-0">
      {$_("IELTS_" + skill + "_LEVEL" + bandLevel)}
    </p>
  {/if}
</div>

<!-- On screen "How this band is awarded" is the band-card tooltip above, which
     a sheet of paper cannot show, so print gets it back as a section. -->
<div class="criteria-print ielts-avoid-break">
  <div class="feedback-label">{$_("IELTS_HOW_THIS_IS_ASSESSED")}</div>
  <p class="feedback-text mb-0">
    {$_("IELTS_" + skill + "_CRITERIA")}
    {$_("IELTS_BAND_SCALE_NOTE")}
  </p>
</div>

<style>
  .report-title {
    font-size: 1.9rem;
    font-weight: 700;
    color: #1f2937;
  }

  .candidate-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
  }

  .candidate-details {
    min-width: 0;
  }

  .candidate-name {
    font-weight: 700;
    color: #1f2937;
  }

  .band-card {
    position: relative;
    flex: 0 0 14rem;
    width: 14rem;
    text-align: center;
    border: 2px solid #0056b3;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
  }

  .band-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #6b7280;
  }

  .band-score {
    font-size: 5rem;
    font-weight: 700;
    line-height: 1.05;
    color: #0056b3;
  }

  .band-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: #4b5563;
  }

  /* "How this band is awarded" hangs off the band label rather than taking a
     section of its own: it explains the number it sits next to, and it is the
     same text on every report of a given skill. */
  .band-info {
    display: inline-block;
    margin-left: 0.3rem;
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    line-height: 1;
    color: #9ca3af;
    cursor: help;
  }

  .band-info:hover {
    color: #0056b3;
  }

  .band-info:focus-visible {
    color: #0056b3;
    outline: 2px solid #0056b3;
    outline-offset: 2px;
    border-radius: 0.125rem;
  }

  .band-tooltip {
    /* Positioned against the band card, not the icon, so the bubble clears the
       score it is explaining instead of covering it. Anchored right because
       the card sits at the right of the report, where a centred bubble would
       run off the page. */
    position: absolute;
    top: calc(100% + 0.5rem);
    right: -0.125rem;
    z-index: 20;
    width: 20rem;
    max-width: 70vw;
    padding: 0.6rem 0.75rem;
    border-radius: 0.375rem;
    background: #1f2937;
    color: #f3f4f6;
    font-size: 0.75rem;
    font-weight: 400;
    letter-spacing: normal;
    line-height: 1.6;
    text-align: left;
    text-transform: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.12s ease-in-out;
  }

  .band-info:hover .band-tooltip,
  .band-info:focus-visible .band-tooltip {
    opacity: 1;
    visibility: visible;
  }

  .band-tooltip-title {
    display: block;
    margin-bottom: 0.25rem;
    font-weight: 700;
    color: #ffffff;
  }

  .feedback {
    margin-top: 1.5rem;
  }

  .feedback-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.7rem;
    color: #6b7280;
    margin-top: 1rem;
  }

  .feedback-label:first-child {
    margin-top: 0;
  }

  .feedback-text {
    color: #4b5563;
    line-height: 1.7;
    margin-bottom: 0;
  }

  /* Print-only: hidden on screen, where the tooltip carries this text. */
  .criteria-print {
    display: none;
  }

  @media (max-width: 768px) {
    .candidate-row {
      flex-direction: column;
      align-items: stretch;
    }

    .band-card {
      flex: 0 0 auto;
      width: 100%;
    }

    .band-score {
      font-size: 4rem;
    }
  }

  @media print {
    /* A hover bubble means nothing on paper: hide the trigger and print the
       criteria as the section they used to be. */
    .band-info {
      display: none;
    }

    .criteria-print {
      display: block;
      margin-top: 1rem;
    }
  }
</style>
